import { useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { MobileBookingBar } from "@/components/booking/BookingBar";
import { SmoothScroll, scrollToTopNow } from "@/components/common/SmoothScroll";

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { scrollToTopNow(); }, [pathname]);
  return null;
};

/** Pages whose first screen is a full-bleed photo – the header sits transparent on top of it */
const PHOTO_HERO_PAGES = ["/", "/rom", "/mat", "/arrangement", "/praktisk", "/om-oss"];
/** Nested routes that open with a photo hero as well (room detail pages) */
const PHOTO_HERO_PREFIXES = ["/rom/"];
const expectsPhotoHero = (pathname) => PHOTO_HERO_PAGES.includes(pathname) || PHOTO_HERO_PREFIXES.some((p) => pathname.startsWith(p));

/**
 * The header may only go transparent when a real photo hero is on the page. We start from the
 * route list (no flicker on first paint) and then confirm against the DOM – any page that renders
 * without a `[data-photo-hero]` element gets the solid header, so the nav is always legible.
 */
const useHasPhotoHero = (pathname) => {
  const expected = expectsPhotoHero(pathname);
  const [has, setHas] = useState(expected);
  useLayoutEffect(() => {
    const check = () => setHas(!!document.querySelector("[data-photo-hero]"));
    check();
    // Content pages render their hero after the content API answers – keep watching the DOM.
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [pathname]);
  return expected && has;
};

export const SiteLayout = () => {
  const { pathname } = useLocation();
  const bookingFlow = pathname.startsWith("/bestill") || pathname.startsWith("/mi-bestilling");
  // Room detail pages carry their own room-specific bottom bar on phones
  const roomDetail = /^\/rom\/.+/.test(pathname);
  const showSearch = pathname === "/" || pathname === "/rom";
  const overlay = useHasPhotoHero(pathname);
  return (
    <div className="min-h-screen bg-background">
      <SmoothScroll />
      <Header showSearch={showSearch} overlay={overlay} />
      <main className={overlay ? "" : "pt-20 lg:pt-24"}>
        <Outlet />
      </main>
      <Footer compact={bookingFlow} />
      {!bookingFlow && !roomDetail && <MobileBookingBar />}
    </div>
  );
};
